import { useState } from 'react'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { JournalEditor } from './journal-editor.tsx'
import { LedgerChart } from './ledger-chart.tsx'
import {
	formatMoney,
	net,
	type ChartSeries,
	type JournalLine,
} from './ledger.ts'
import { TimeTravelSlider } from './time-travel-slider.tsx'

const SEED: JournalLine[] = [
	{ id: 'm1', date: '2026-01-31', kind: 'revenue', amount: 1200 },
	{ id: 'm2', date: '2026-02-28', kind: 'expense', amount: 400 },
	{ id: 'm3', date: '2026-03-31', kind: 'revenue', amount: 900 },
]

export function MutableBook() {
	const [lines, setLines] = useState<JournalLine[]>(SEED)
	const [edits, setEdits] = useState(0)

	function onChange(next: JournalLine[]) {
		setLines(next)
		setEdits((e) => e + 1)
	}

	const series: ChartSeries[] = [
		{ key: 'current', label: 'Current', color: '#ef4444', lines },
	]

	return (
		<Card className="border-rose-500/40">
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500" />
					Mutable book
				</CardTitle>
				<CardDescription>
					Only one book exists. Every edit <strong>overwrites in place</strong>{' '}
					— the chart shows just the present, and the evidence the books ever
					said something else is gone.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<JournalEditor lines={lines} onChange={onChange} />

				<div className="rounded-md border px-3 py-2 text-left text-sm">
					Net:{' '}
					<strong
						className={
							net(lines) >= 0
								? 'text-emerald-600 dark:text-emerald-400'
								: 'text-rose-600 dark:text-rose-400'
						}
					>
						{formatMoney(net(lines))}
					</strong>
				</div>

				<LedgerChart series={series} />

				<TimeTravelSlider
					label="Time-travel: as of knowledge-time…"
					hint="nothing to travel to"
					min={0}
					max={0}
					value={0}
					disabled
				/>
				<p className="text-muted-foreground text-xs">
					The slider is inert: there is no history.{' '}
					<strong className="text-rose-600 dark:text-rose-400">
						{edits} edit{edits === 1 ? '' : 's'} → {edits} past state
						{edits === 1 ? '' : 's'} unrecoverable.
					</strong>
				</p>

				<p className="text-muted-foreground border-t pt-3 text-xs">
					<strong>The trap:</strong> it looks tidy — one clean line, no clutter.
					That tidiness <em>is</em> the data loss.
				</p>
			</CardContent>
		</Card>
	)
}
